<script setup lang="ts">
import { reactivePick, useEventListener } from '@vueuse/core';
import { ToastProvider, ToastViewport, useForwardProps } from 'reka-ui';
import { computed, ref } from 'vue';

import { KaToast } from '@/kasten-components/components/KaToast';
import { useDataTestid } from '@/kasten-components/composables';
import { FALLBACK_TOAST_HEIGHT, FALLBACK_TOAST_OFFSET } from '@/kasten-components/utils';
import { cnMerge } from '@/utils/utils.js';

import { toasterVariants } from './toaster-variants';
import type { KaToasterProps } from './types';
export type { KaToasterProps, KaToastItem } from './types';

defineOptions({ name: 'KaToaster' });

const props = withDefaults(defineProps<KaToasterProps>(), {
  position: 'top-center',
  toasts: () => [],
  maxToasts: 5,
  duration: 5000,
  label: 'Notifications',
  hotkey: () => ['F8'],
  class: '',
});

const emit = defineEmits<{
  remove: [id: string | number];
}>();

const { dataTestid } = useDataTestid();

const swipeDirection = computed(() => {
  switch (props.position) {
    case 'top-center':
      return 'up';
    case 'top-right':
    case 'bottom-right':
      return 'right';
    case 'bottom-center':
      return 'down';
    case 'top-left':
    case 'bottom-left':
      return 'left';
  }
  return 'right';
});

const visibleToasts = computed(() => {
  return props.toasts.slice(-props.maxToasts);
});

// Track toast heights for accurate positioning
const toastRefs = ref<Array<{ height: number }>>([]);

// Calculate offset for each toast based on toasts above it
function getOffset(index: number) {
  const offset = toastRefs.value.slice(index + 1).reduce((acc, ref, i) => {
    // Get the actual toast index in visibleToasts
    const toastIndex = index + 1 + i;
    const toast = visibleToasts.value[toastIndex];

    // Skip height of closing toasts they should be excluded immediately
    if (toast && toast.open === false) {
      return acc;
    }

    return acc + (ref?.height || FALLBACK_TOAST_HEIGHT) + FALLBACK_TOAST_OFFSET;
  }, 0);
  // For bottom positions, add the height of the current toast to ensure it's visible
  if (props.position?.startsWith('bottom')) {
    const currentToastHeight = toastRefs.value[index]?.height || FALLBACK_TOAST_HEIGHT;
    return offset + currentToastHeight;
  }
  return offset;
}

function handleToastClose(id: string | number | undefined) {
  if (id !== undefined) {
    emit('remove', id);
  }
}

function onUpdateOpen(value: boolean, id: string | number) {
  if (!value) {
    // Guard: non-closable toasts should never be removed.
    // reka-ui's internal escape handler fires update:open(false) for all toasts,
    // but since `open` is prop-controlled, the toast stays rendered as long as
    // the parent keeps it in the array.
    const toast = visibleToasts.value.find((t) => t.id === id);
    if (toast && toast.closable === false) return;
    handleToastClose(id);
  }
}

// Forward props for ToastProvider
const providerProps = useForwardProps(reactivePick(props, 'duration', 'label'));

// Intercept Escape in capture phase so it closes only the topmost toast
// and never simultaneously triggers headlessui/reka-ui close handlers.
// We use stopImmediatePropagation because reka-ui's onKeyStroke and headlessui's
// Dialog both register on `document` — stopPropagation alone doesn't prevent
// other listeners on the same target from firing.
useEventListener(
  document,
  'keydown',
  (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    const openToasts = visibleToasts.value.filter((t) => t.open !== false);
    if (!openToasts.length) return;

    // Find the topmost closable toast (last in array = rendered on top).
    const top = [...openToasts].reverse().find((t) => t.closable !== false);

    // Only intercept when there's actually a closable toast to dismiss.
    // If all toasts are non-closable, let the event through so modals/sidepanels
    // can still respond to Escape normally.
    if (!top) return;

    e.stopImmediatePropagation();
    e.preventDefault();
    emit('remove', top.id);
  },
  { capture: true },
);
</script>

<template>
  <ToastProvider :swipe-direction="swipeDirection" v-bind="providerProps">
    <slot />

    <KaToast
      v-for="(toast, index) of visibleToasts"
      :key="toast.id"
      ref="toastRefs"
      v-bind="toast"
      :position="props.position"
      :style="{
        '--transform': props.position?.startsWith('top')
          ? `translateY(${getOffset(index)}px)`
          : `translateY(-${getOffset(index)}px)`,
        transform: 'var(--transform)',
        'z-index': props.position?.startsWith('top')
          ? index + 100
          : visibleToasts.length - index + 100,
      }"
      @update:open="onUpdateOpen($event, toast.id)"
      @click="toast.onClick && toast.onClick(toast)"
    />

    <ToastViewport
      :data-testid="dataTestid"
      :data-position="props.position"
      :class="cnMerge(toasterVariants({ position: props.position }), props.class)"
      :hotkey="props.hotkey"
      :label="`${props.label} (${props.hotkey.join('+')})`"
    />
  </ToastProvider>
</template>

<style>
@import url('../../assets/toast-animations.css');
</style>
